<template>
  <v-container fluid class="settings-page">
    <!-- Header -->
    <div class="settings-header">
      <h1 class="settings-header__title">Settings</h1>
      <p class="settings-header__subtitle">Configure and manage HRDC system settings</p>
    </div>

    <!-- Settings Grid -->
    <div class="settings-grid">
      <div
        v-for="(item, i) in items"
        :key="i"
        class="settings-card"
        @click="navigateSetting(item.route)"
      >
        <div class="settings-card__content">
          <div class="settings-card__top">
            <div class="settings-card__icon" :style="{ background: item.accentColor + '14', color: item.accentColor }">
              <v-icon :color="item.accentColor" size="24">{{ item.mdiIcon }}</v-icon>
            </div>
            <div class="settings-card__info">
              <h3 class="settings-card__name">{{ item.title }}</h3>
              <p class="settings-card__desc">{{ item.description }}</p>
            </div>
          </div>
          <div class="settings-card__bottom">
            <span class="settings-card__action">Open Settings</span>
            <v-icon size="16" class="settings-card__arrow" color="#c0c7cf">mdi-arrow-right</v-icon>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import beforeEnterRoleCheck from "@/services/hrdc/objects/beforeEnterRoleCheck";

export default {
  mixins: [beforeEnterRoleCheck],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  data: () => ({
    items: [
      // Job & XERO Token moved to the Developer Console (/en/developer).
      {
        accentColor: "#f59e0b",
        title: "Attachment",
        description: "Manage static attachment",
        route: "HrdcSettingsAttachment",
        mdiIcon: "mdi-paperclip",
      },
      {
        accentColor: "#10b981",
        title: "Survey",
        description: "Manage survey",
        route: "HrdcSettingsSurvey",
        mdiIcon: "mdi-chat-question",
      },
      // {
      //   accentColor: "#ef4444",
      //   title: "Companies (temporary)",
      //   description: "Manage company",
      //   route: "HrdcSettingsCompany",
      //   mdiIcon: "mdi-office-building",
      // },
    ],
  }),
  async created() {
    this.checkRoles(this.auth.roles);
  },
  methods: {
    navigateSetting(route) {
      this.$router.push({
        name: route,
      });
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored — do NOT regenerate this from the light rules below. Same
   system as the console dashboard (see the DARK MODE block in
   src/views/console/PageConsoleDashboard.vue): the surface ramp does the
   separating, the edge is --app-border rather than the light theme's
   near-white hairline, and depth is shadow + a 1px inner top highlight.

   Colours are token references only. No literal hexes.
   ============================================================================ */

.dark-mode .settings-page {
  background-color: var(--app-page);
  background-image: var(--app-page-glow);
}

.dark-mode .settings-header__title {
  color: var(--app-text);
}

.dark-mode .settings-header__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .settings-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 4px 14px var(--app-shadow-mid);
}

.dark-mode .settings-card:hover {
  background: var(--app-elevated);
  border-color: var(--app-accent-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 12px 28px var(--app-shadow);
}

/* The tile's fill is written inline as the setting's accent + '14' (8% alpha),
   tuned against white; on charcoal it disappears. The template also writes
   that accent as the tile's own `color`, so currentColor repaints the fill at
   a workable strength without the page needing to know the active theme. */
.dark-mode .settings-card__icon {
  background: transparent !important;
  position: relative;
}

.dark-mode .settings-card__icon::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0.18;
}

.dark-mode .settings-card__icon .v-icon {
  position: relative;
}

.dark-mode .settings-card__name {
  color: var(--app-text);
}

.dark-mode .settings-card__desc {
  color: var(--app-text-muted);
}

.dark-mode .settings-card__action {
  color: var(--app-text-faint);
}

.dark-mode .settings-card:hover .settings-card__action {
  color: var(--app-accent);
}

/* Written inline as a Vuetify `color` prop, so it takes !important to beat. */
.dark-mode .settings-card__arrow {
  color: var(--app-text-faint) !important;
}

.dark-mode .settings-card:hover .settings-card__arrow {
  color: var(--app-accent) !important;
}

/* ===== end DARK MODE ===== */

.settings-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header */
.settings-header {
  margin-bottom: 32px;
}

.settings-header__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.settings-header__subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

/* Grid */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* Card */
.settings-card {
  background: #ffffff; border: 1px solid rgba(226, 232, 240, 0.5);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  display: flex;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
}

.settings-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* Accent */


/* Content */
.settings-card__content {
  flex: 1;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Top */
.settings-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.settings-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #e0e3e7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.settings-card__info {
  flex: 1;
  min-width: 0;
}

.settings-card__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.settings-card__desc {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

/* Bottom */
.settings-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.settings-card__action {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.settings-card__arrow {
  transition: all 0.2s ease;
}

.settings-card:hover .settings-card__arrow {
  color: #0ea5e9 !important;
  transform: translateX(3px);
}

.settings-card:hover .settings-card__action {
  color: #0ea5e9;
}

/* Mobile */
@media (max-width: 599px) {
  .settings-page {
    padding: 20px 16px !important;
  }

  .settings-header__title {
    font-size: 1.2rem;
  }

  .settings-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .settings-card__content {
    padding: 14px 16px;
    gap: 10px;
  }

  .settings-card__icon {
    width: 36px;
    height: 36px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .settings-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}
</style>
